import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './MatrixMode.css';

export interface MatrixPest {
  id: string;
  code: string;
  name: string;
  actionThreshold?: number;
}

export interface MatrixModeProps {
  incPests: MatrixPest[];
  cellState: (point: number, pestId: string) => 'empty' | 'clean' | 'infested';
  pctFor: (pestId: string) => number;
  treeInfestedCount: (tree: number, pestId: string) => number;
  isOver: (pestId: string) => boolean;
  onCycle: (point: number, pestId: string) => void;
  /** Mark a whole point examined with every pest clean (green). */
  onMarkClean?: (point: number) => void;
}

const TREES = 10;
const PER_TREE = 10;

/** Tier class for infested-count cells: 0→none, 1-2→c1, 3-4→c2, 5+→c3 */
function tierClass(n: number): string {
  if (n === 0) return '';
  if (n <= 2) return 'tcount--c1';
  if (n <= 4) return 'tcount--c2';
  return 'tcount--c3';
}

export function MatrixMode({
  incPests,
  cellState,
  pctFor,
  treeInfestedCount,
  isOver,
  onCycle,
  onMarkClean,
}: MatrixModeProps) {
  const { t } = useTranslation();

  // Local collapse state — pure UI, not derived from hook
  const [collapsed, setCollapsed] = useState<Set<number>>(new Set());
  const [allCollapsed, setAllCollapsed] = useState(false);

  const handleToggleAll = useCallback(() => {
    if (allCollapsed) {
      setCollapsed(new Set());
      setAllCollapsed(false);
    } else {
      setCollapsed(new Set(Array.from({ length: TREES }, (_, i) => i)));
      setAllCollapsed(true);
    }
  }, [allCollapsed]);

  const handleToggleTree = useCallback((treeIdx: number) => {
    setCollapsed(prev => {
      const next = new Set(prev);
      if (next.has(treeIdx)) {
        next.delete(treeIdx);
      } else {
        next.add(treeIdx);
      }
      // Keep allCollapsed in sync
      if (next.size === TREES) {
        setAllCollapsed(true);
      } else {
        setAllCollapsed(false);
      }
      return next;
    });
  }, []);

  return (
    <div className="matrix-wrap">
      {/* Toolbar */}
      <div className="matrix-tools">
        <button
          className="btn btn-outline-secondary btn-sm"
          data-testid="toggle-all"
          onClick={handleToggleAll}
        >
          {allCollapsed
            ? t('fieldScout.desktop.matrix.expandAll')
            : t('fieldScout.desktop.matrix.collapseAll')}
        </button>
      </div>

      {/* Sticky table */}
      <div className="matrix-scroll">
        <table className="mx">
          {/* HEAD — sticky top */}
          <thead>
            <tr>
              <th className="pt">{t('fieldScout.desktop.matrix.pointTree')}</th>
              {incPests.map(pest => (
                <th key={pest.id}>
                  <span className="pcode">{pest.code}</span>
                  <span className={`pct${isOver(pest.id) ? ' over' : ''}`}>
                    {pctFor(pest.id)}%
                  </span>
                </th>
              ))}
            </tr>
          </thead>

          {/* BODY — 10 trees × 10 points */}
          <tbody>
            {Array.from({ length: TREES }, (_, treeIdx) => {
              const isTreeCollapsed = collapsed.has(treeIdx);
              const totalInfested = incPests.reduce(
                (sum, pest) => sum + treeInfestedCount(treeIdx, pest.id),
                0
              );

              return (
                <React.Fragment key={treeIdx}>
                  {/* Tree header row */}
                  <tr
                    className={`tree-hd${isTreeCollapsed ? ' collapsed' : ''}`}
                    data-tree={treeIdx}
                  >
                    <td className="pt" onClick={() => handleToggleTree(treeIdx)}>
                      <span className="caret">{isTreeCollapsed ? '▸' : '▾'}</span>{' '}
                      <b>{t('fieldScout.desktop.matrix.treeLabel', { n: treeIdx + 1 })}</b>
                      <span className="tmeta">
                        {t('fieldScout.desktop.matrix.infestedMarks', { count: totalInfested })}
                      </span>
                    </td>
                    {incPests.map(pest => {
                      const count = treeInfestedCount(treeIdx, pest.id);
                      const tc = tierClass(count);
                      return (
                        <td
                          key={pest.id}
                          className={`tcount${tc ? ` ${tc}` : ''}`}
                          data-testid={`tree-count-${treeIdx}-${pest.id}`}
                        >
                          {count || '·'}
                        </td>
                      );
                    })}
                  </tr>

                  {/* Point rows — hidden when tree is collapsed */}
                  {!isTreeCollapsed &&
                    Array.from({ length: PER_TREE }, (_, k) => {
                      const pointIdx = treeIdx * PER_TREE + k;
                      const pointLabel = `P-${String(pointIdx + 1).padStart(3, '0')}`;

                      return (
                        <tr key={pointIdx} className="pt-row" data-tree={treeIdx}>
                          <td className="pt">
                            {onMarkClean && (
                              <button
                                type="button"
                                className="row-clean-btn"
                                data-testid={`row-clean-${pointIdx}`}
                                title={t('fieldScout.desktop.markAllClean')}
                                aria-label={t('fieldScout.desktop.markAllClean')}
                                onClick={() => onMarkClean(pointIdx)}
                              >
                                ✓
                              </button>
                            )}
                            <b>{pointLabel}</b>{' '}
                            <span className="mono">T{treeIdx + 1}·{k + 1}</span>
                          </td>
                          {incPests.map(pest => {
                            const state = cellState(pointIdx, pest.id);
                            return (
                              <td
                                key={pest.id}
                                className={`cell ${state}`}
                                data-testid={`cell-${pointIdx}-${pest.id}`}
                                onClick={() => onCycle(pointIdx, pest.id)}
                                tabIndex={0}
                                onKeyDown={(e: React.KeyboardEvent<HTMLTableCellElement>) => {
                                  if (e.key === ' ') {
                                    e.preventDefault();
                                    onCycle(pointIdx, pest.id);
                                  } else if (e.key === 'Enter') {
                                    e.preventDefault();
                                    // Move focus to same pest column on next visible point
                                    const totalPoints = TREES * PER_TREE;
                                    let next = pointIdx + 1;
                                    while (next < totalPoints) {
                                      const nextTree = Math.floor(next / PER_TREE);
                                      if (!collapsed.has(nextTree)) break;
                                      next++;
                                    }
                                    if (next < totalPoints) {
                                      const target = document.querySelector(
                                        `[data-testid="cell-${next}-${pest.id}"]`
                                      ) as HTMLElement | null;
                                      target?.focus();
                                    }
                                  }
                                }}
                              >
                                <div className="mk" />
                              </td>
                            );
                          })}
                        </tr>
                      );
                    })}
                </React.Fragment>
              );
            })}
          </tbody>

          {/* FOOT — sticky bottom */}
          <tfoot>
            <tr>
              <td className="pt">{t('fieldScout.desktop.matrix.footerLabel')}</td>
              {incPests.map(pest => (
                <td
                  key={pest.id}
                  className={isOver(pest.id) ? 'over' : undefined}
                >
                  {pctFor(pest.id)}%
                </td>
              ))}
            </tr>
          </tfoot>
        </table>
      </div>

      {/* Scroll note */}
      <div className="scrollnote">{t('fieldScout.desktop.matrix.scrollNote')}</div>
    </div>
  );
}

export default MatrixMode;
